import { useEffect, useCallback, useState } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import {
  applePayCredentialsAtom,
  canPayWithApplePayAtom,
  venueAtom,
  userInfoAtom,
  paymentMethodsAtom,
  applePayUserAtom,
} from '@/state/global'
import { PaymentDataAtom, PaymentData } from '@/state/payment'
import { PaymentMethodInputTypes } from '@/utils/constants'
import { ApplePayUser } from '@/utils/types'

// Module-level singletons — persist across component unmount/remount cycles.
// Only one TEConnect instance should ever exist per session.
let globalTeConnect: any = null
let globalTecPrInterface: any = null
let globalIsInitialized = false
let globalListenerRegistered = false
let globalInitGeneration = 0

// Module-level pending promise resolution — shared across all hook instances so that
// the single listenFor callback always resolves the correct promise regardless of
// which component called handleMagensaApplePayClick.
let globalPendingResolve: ((result: MagensaApplePayResult) => void) | null = null
let globalPendingReject: ((error: any) => void) | null = null

// Carries the total amount set by the caller into the payment-method-selection
// event handler so the Apple Pay sheet displays the correct total.
let globalPendingTotalAmount: string | null = null

// Points to the most recently mounted instance's confirm-token handler.
// Updated via useEffect so the single listener always calls a fresh closure
// with up-to-date Jotai atom values (storedUser, paymentMethods, etc.).
let globalConfirmTokenHandler: ((data: any) => void) | null = null

const HIDDEN_BUTTON_ID = 'magensa-applepay-hidden-button'

// TEConnect mounts the Apple Pay button as a <div id="te-connect-apple-pay-btn">
// (not a <button>), so we must query by its known id.
function findApplePayButton(container: HTMLElement): HTMLElement | null {
  return (
    (container.querySelector('#te-connect-apple-pay-btn') as HTMLElement | null) ??
    (container.querySelector('.apple-pay-button') as HTMLElement | null)
  )
}

type MagensaApplePayResult = {
  paymentMethod: PaymentData
  paymentUser: ApplePayUser
}

type UseMagensaApplePayReturn = {
  handleMagensaApplePayClick: (totalAmount?: string) => Promise<MagensaApplePayResult>
  isMagensaApplePaySupported: boolean
  isMagensaApplePayInitialized: boolean
}

// Imperatively create and append the hidden container to document.body once.
// This keeps it outside of React's control so re-renders can never replace the
// DOM node or the SDK button mounted inside it.
function ensureHiddenContainer(): HTMLElement {
  let container = document.getElementById(HIDDEN_BUTTON_ID)
  if (!container) {
    container = document.createElement('div')
    container.id = HIDDEN_BUTTON_ID
    container.setAttribute('aria-hidden', 'true')
    Object.assign(container.style, {
      position: 'absolute',
      left: '-9999px',
      top: '0',
      width: '150px',
      height: '50px',
      overflow: 'hidden',
    })
    document.body.appendChild(container)
  }
  return container
}

export function useMagensaApplePay(): UseMagensaApplePayReturn {
  const venue = useAtomValue(venueAtom)
  const applePayCredentials = useAtomValue(applePayCredentialsAtom)
  const canPayWithApplePay = useAtomValue(canPayWithApplePayAtom)
  const storedUser = useAtomValue(userInfoAtom)
  const setApplePayUser = useSetAtom(applePayUserAtom)
  const [paymentMethods] = useAtom(paymentMethodsAtom)
  const [, setPaymentMethod] = useAtom(PaymentDataAtom)

  const [isInitialized, setIsInitialized] = useState(globalIsInitialized)

  const isMagensaApplePaySupported =
    canPayWithApplePay && venue?.payment_processor === 'magensa_unigate' && !!venue?.apple_pay_enabled

  const handleConfirmToken = useCallback(
    (data: any) => {
      const { tokenDetails, completePayment, error } = data

      if (error) {
        console.error('[MagensaAPay] confirm-token error:', error)
        completePayment?.('failure')
        globalPendingReject?.(error)
        globalPendingResolve = null
        globalPendingReject = null
        globalPendingTotalAmount = null
        return
      }

      // Token structure for Apple Pay via TEConnect:
      //   tokenDetails.token          — { paymentData, paymentMethod, transactionIdentifier }
      //   tokenDetails.shippingContact — { givenName, familyName, emailAddress, phoneNumber }
      const applePaymentMethod = tokenDetails?.token?.paymentMethod
      const shippingContact = tokenDetails?.shippingContact

      // displayName is e.g. "Visa 0661" — last word is the last 4 digits
      const displayName: string = applePaymentMethod?.displayName ?? ''
      const lastFour = displayName.split(' ').pop() ?? ''

      const first_name = shippingContact?.givenName || storedUser?.first_name || ''
      const last_name = shippingContact?.familyName || storedUser?.last_name || ''

      const card_token = JSON.stringify(tokenDetails?.token ?? '')

      const paymentMethod: PaymentData = {
        network: (applePaymentMethod?.network ?? '').toLowerCase(),
        name: `${last_name.toUpperCase()}/${first_name.toUpperCase()}`,
        pan_mask: `************${lastFour}`,
        input_mode: PaymentMethodInputTypes.MAGENSA_UNIGATE_APPLE_PAY_WEB,
        card_token,
      }

      // Normalize phone — Apple Pay omits the country code for US numbers
      const rawPhone = shippingContact?.phoneNumber ?? ''
      const digitsOnly = rawPhone.replace(/\D/g, '')
      const cleanPhone =
        digitsOnly.length === 10
          ? `+1${digitsOnly}`
          : digitsOnly.length === 11 && digitsOnly.startsWith('1')
          ? `+${digitsOnly}`
          : ''

      const paymentUser: ApplePayUser = {
        first_name,
        last_name,
        email: shippingContact?.emailAddress ?? '',
        phone: cleanPhone,
      }

      // completePayment must be called within 30 seconds or Apple closes the sheet
      // with a failure. We call it immediately on token receipt — the actual charge
      // happens after this hook resolves to the calling component.
      completePayment('success')

      localStorage.setItem('paymentMethods', JSON.stringify([paymentMethod, ...paymentMethods]))
      setPaymentMethod(paymentMethod)

      if (!storedUser?.phone) {
        setApplePayUser(paymentUser)
      }

      globalPendingResolve?.({ paymentMethod, paymentUser })
      globalPendingResolve = null
      globalPendingReject = null
      globalPendingTotalAmount = null
    },
    [storedUser, paymentMethods]
  )

  // Keep the module-level handler pointer current so the single registered listener
  // always invokes a fresh closure with up-to-date atom values.
  useEffect(() => {
    globalConfirmTokenHandler = handleConfirmToken
  }, [handleConfirmToken])

  // Initialize TEConnect and mount button into the hidden container.
  // Runs when credentials are available. Creates the singleton instance once;
  // creates a fresh interface on each mount (per TEConnect best practice).
  useEffect(() => {
    if (!isMagensaApplePaySupported) return
    if (!venue?.magensa_unigate_teconnect_public_key) {
      console.warn('[MagensaAPay] init skipped — magensa_unigate_teconnect_public_key missing')
      return
    }
    if (!applePayCredentials?.merchantId) {
      console.warn('[MagensaAPay] init skipped — merchantId missing from applePayCredentials')
      return
    }

    globalIsInitialized = false
    setIsInitialized(false)

    const myGeneration = ++globalInitGeneration

    let isMounted = true
    const isSuperseded = () => myGeneration !== globalInitGeneration || !isMounted

    const initTEConnect = async () => {
      try {
        const [{ createTEConnect }, { TeConnectJs }] = await Promise.all([
          import('@magensa/te-connect'),
          import('@magensa/te-connect-js'),
        ])
        if (isSuperseded()) return

        await new Promise((r) => setTimeout(r, 0))
        if (isSuperseded()) return

        if (!globalTeConnect) {
          const teConnectInstance = createTEConnect(venue.magensa_unigate_teconnect_public_key as string, {
            tecPaymentRequest: {
              appleMerchantId: applePayCredentials.merchantId,
            },
          })
          globalTeConnect = new TeConnectJs(teConnectInstance)
        }

        if (isSuperseded()) return

        const applePaymentRequest = {
          storeDisplayName: venue.name ?? 'Union',
          currencyCode: 'USD',
          countryCode: 'US',
          supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
          merchantCapabilities: ['supports3DS'],
          total: {
            label: venue.name ?? 'Union',
            amount: '0.00',
            type: 'pending',
          },
          requiredShippingContactFields: storedUser ? [] : ['name', 'phone', 'email'],
        }

        if (!globalTecPrInterface) {
          globalTecPrInterface = globalTeConnect.createTecPaymentRequest(applePaymentRequest)
          globalListenerRegistered = false
        }

        const canMakePaymentsResult = await globalTecPrInterface.canMakePayments()

        if (!canMakePaymentsResult || !canMakePaymentsResult.applePay) {
          console.warn(
            '[MagensaAPay] Apple Pay unavailable via TEConnect — domain not verified with Magensa? Result:',
            canMakePaymentsResult
          )
          return
        }

        if (isSuperseded()) return

        if (!globalListenerRegistered) {
          globalTecPrInterface.listenFor('confirm-token', (data: any) => {
            globalConfirmTokenHandler?.(data)
          })
          globalTecPrInterface.listenFor('payment-method-selection', (event: any) => {
            const { completePaymentMethodSelection } = event
            completePaymentMethodSelection({
              newTotal: {
                label: venue.name ?? 'Union',
                amount: globalPendingTotalAmount ?? '0.00',
                type: globalPendingTotalAmount ? 'final' : 'pending',
              },
            })
          })
          globalListenerRegistered = true
        }

        const buttonOptions = {
          applePayOptions: {
            buttonStyle: 'black',
            buttonType: 'plain',
          },
        }

        const container = ensureHiddenContainer()
        const existingBtn = findApplePayButton(container)

        if (!existingBtn) {
          globalTecPrInterface.mountTecPrButton(HIDDEN_BUTTON_ID, buttonOptions)

          let found = false
          for (let i = 0; i < 50; i++) {
            if (isSuperseded()) return
            if (findApplePayButton(container)) {
              found = true
              break
            }
            await new Promise((r) => setTimeout(r, 100))
          }
          if (!found) {
            console.error('[MagensaAPay] button not found after mount — init incomplete')
            return
          }
        }

        if (isSuperseded()) return

        const finalBtn = findApplePayButton(container)
        if (!finalBtn) {
          console.error('[MagensaAPay] button not found after mount — init incomplete')
          return
        }

        globalIsInitialized = true
        if (isMounted) setIsInitialized(true)
      } catch (err) {
        console.error('[MagensaAPay] init threw an exception:', err)
      }
    }

    initTEConnect()

    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMagensaApplePaySupported, venue?.magensa_unigate_teconnect_public_key, applePayCredentials?.merchantId])

  const handleMagensaApplePayClick = useCallback((totalAmount?: string): Promise<MagensaApplePayResult> => {
    return new Promise((resolve, reject) => {
      if (!globalIsInitialized || !globalTecPrInterface) {
        const msg = 'Magensa TEConnect Apple Pay is not initialized'
        console.error('[MagensaAPay]', msg)
        reject(new Error(msg))
        return
      }

      if (globalPendingReject) {
        globalPendingReject(new Error('Payment request superseded'))
      }

      globalPendingResolve = resolve
      globalPendingReject = reject
      globalPendingTotalAmount = totalAmount ?? null

      const container = document.getElementById(HIDDEN_BUTTON_ID)
      const btn = container ? findApplePayButton(container) : null

      if (!btn) {
        const msg = 'TEConnect Apple Pay button not found in DOM'
        console.error('[MagensaAPay]', msg)
        reject(new Error(msg))
        globalPendingResolve = null
        globalPendingReject = null
        return
      }

      // Click synchronously — still within the user gesture activation window
      btn.click()
    })
  }, [])

  return {
    handleMagensaApplePayClick,
    isMagensaApplePaySupported,
    isMagensaApplePayInitialized: isInitialized,
  }
}
